import type { JSX } from "react";
import { type ComponentPropsWithoutRef, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import type { PluggableList } from "unified";

interface IOwnProps {
    content: string | null;
    unsafe?: boolean;
}

type IProps = IOwnProps & ComponentPropsWithoutRef<"div">;

function isExternal(href: string): boolean {
    if (!href) {
        return false;
    }

    if (!href.startsWith("/") && !href.includes("://")) {
        const domainMatch = href.match(/^([^/]+)/);
        if (domainMatch?.[1].includes(".")) {
            return domainMatch[1] !== window.location.hostname;
        }
    }

    try {
        const u = new URL(href, window.location.href);
        return (u.protocol === "http:" || u.protocol === "https:") && u.origin !== window.location.origin;
    } catch {
        return false;
    }
}

function RenderMark({ content, unsafe = false, ...props }: IProps): JSX.Element {
    const components: Components = {
        a: ({ href, ...rest }) => {
            const external = isExternal(href);
            return (
                <a
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    {...rest}
                />
            );
        },
    };

    const rehypePlugins: PluggableList = unsafe ? [rehypeRaw] : [];

    return (
        <div {...props}>
            <ReactMarkdown rehypePlugins={rehypePlugins} components={components}>
                {content ?? ""}
            </ReactMarkdown>
        </div>
    );
}

export default memo(RenderMark);
